import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '../../hooks/useTheme';
import Switch from '../common/Switch';
import Text from '../common/Text';
import Card from '../common/Card';

interface PublicBoardSwitchProps {
  initialValue?: boolean;
  onToggle?: (isPublic: boolean) => void;
  disabled?: boolean;
}

/**
 * "Make this board public" switch component using basic Switch
 */
const PublicBoardSwitch: React.FC<PublicBoardSwitchProps> = ({
  initialValue = false,
  onToggle,
  disabled = false,
}) => {
  useTheme(); // colors reserved for future styling
  const [isPublic, setIsPublic] = useState(initialValue);

  const handleToggle = (value: boolean) => {
    setIsPublic(value);
    onToggle?.(value);
  };

  return (
    <Card variant="outlined" padding="md" margin="sm">
      <View style={styles.container}>
        <View style={styles.textContainer}>
          <Text weight="medium" size="md">
            Make this board public
          </Text>
          <Text variant="secondary" size="sm" style={styles.description}>
            {isPublic
              ? 'Anyone can view and discover this board'
              : 'Only you can view this board'}
          </Text>
          <Text
            variant={isPublic ? 'success' : 'muted'}
            size="sm"
            weight="medium"
            style={styles.status}
          >
            Status: {isPublic ? 'Public ✅' : 'Private 🔒'}
          </Text>
        </View>

        <Switch
          value={isPublic}
          onValueChange={handleToggle}
          disabled={disabled}
        />
      </View>
    </Card>
  );
};

/**
 * Example usage component showing the PublicBoardSwitch in action
 */
export const PublicBoardSwitchExample: React.FC = () => {
  const { colors } = useTheme();
  const [boardSettings, setBoardSettings] = useState({
    colorBoard: false,
    moodBoard: true,
    styleBoard: false,
  });

  const handleBoardToggle = (boardType: string, isPublic: boolean) => {
    setBoardSettings((prev) => ({
      ...prev,
      [boardType]: isPublic,
    }));
    console.log(`${boardType} is now ${isPublic ? 'public' : 'private'}`);
  };

  return (
    <View
      style={[styles.exampleContainer, { backgroundColor: colors.background }]}
    >
      <Text size="xl" weight="bold" style={styles.title}>
        Board Privacy Settings
      </Text>

      <Text variant="secondary" style={styles.subtitle}>
        Control who can see your boards
      </Text>

      {/* Color Board */}
      <PublicBoardSwitch
        initialValue={boardSettings.colorBoard}
        onToggle={(isPublic) => handleBoardToggle('colorBoard', isPublic)}
      />

      {/* Mood Board */}
      <PublicBoardSwitch
        initialValue={boardSettings.moodBoard}
        onToggle={(isPublic) => handleBoardToggle('moodBoard', isPublic)}
      />

      {/* Style Board */}
      <PublicBoardSwitch
        initialValue={boardSettings.styleBoard}
        onToggle={(isPublic) => handleBoardToggle('styleBoard', isPublic)}
      />

      {/* Disabled Example */}
      <Card variant="outlined" padding="md" margin="sm">
        <View style={styles.container}>
          <View style={styles.textContainer}>
            <Text weight="medium" size="md" variant="muted">
              Make this board public
            </Text>
            <Text variant="secondary" size="sm" style={styles.description}>
              Premium feature - upgrade to make boards public
            </Text>
            <Text
              variant="muted"
              size="sm"
              weight="medium"
              style={styles.status}
            >
              Status: Disabled (Premium Required)
            </Text>
          </View>

          <Switch value={false} onValueChange={() => {}} disabled={true} />
        </View>
      </Card>

      {/* Summary */}
      <Card variant="filled" padding="md" margin="md">
        <Text size="lg" weight="semibold" style={styles.summaryTitle}>
          Current Settings:
        </Text>
        <Text>
          • Colour Board: {boardSettings.colorBoard ? 'Public ✅' : 'Private 🔒'}
        </Text>
        <Text>
          • Mood Board: {boardSettings.moodBoard ? 'Public ✅' : 'Private 🔒'}
        </Text>
        <Text>
          • Style Board: {boardSettings.styleBoard ? 'Public ✅' : 'Private 🔒'}
        </Text>
      </Card>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textContainer: {
    flex: 1,
    marginRight: 16,
  },
  description: {
    marginTop: 4,
    lineHeight: 18,
  },
  status: {
    marginTop: 8,
  },
  exampleContainer: {
    flex: 1,
    padding: 16,
  },
  title: {
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: 24,
  },
  summaryTitle: {
    marginBottom: 8,
  },
});

export default PublicBoardSwitch;
